/**
 * One library on the TV — Live TV, Movies or Series.
 *
 * Two modes, and which one is the default is the whole point of this screen.
 *
 * **Discover** is what opens: the same rows the server builds for the web library screen,
 * rendered as focusable rails. A remote is the worst possible instrument for working
 * through a few hundred provider category names, so on the TV the difference between
 * rows and a category wall is larger than it is anywhere else.
 *
 * **Browse** is the old screen, unchanged and still reachable: provider categories, and a
 * windowed grid behind them. Some categories genuinely are the fastest way to a thing,
 * and a household that has learned where its films live should not lose that.
 *
 * ## Why the windowed grid is still here in full
 *
 * A live library holds twenty thousand channels. On a desktop, appending pages to a grid
 * is fine — the browser handles a few thousand nodes. A 2020-era Tizen panel does not:
 * past roughly a hundred cards, every focus change costs a layout of the whole grid and
 * the remote starts lagging behind the presses. So exactly `WINDOW_ROWS` rows exist at
 * any moment and the rest is not rendered at all.
 *
 * Sliding by individual cards would reflow every row on every press. Sliding by rows
 * keeps each card in its column, so the only thing that changes is which rows exist —
 * which is what makes the layout stable enough for geometric focus scoring to stay
 * correct. That requires knowing how many cards a row holds, so `COLUMNS` is fixed here
 * *and* in the stylesheet (`.tvgrid` is `repeat(6, 1fr)`, not `auto-fill`). The two must
 * agree; a mismatch tears the window.
 *
 * No spacer element is needed to keep the scroll height honest, because the focused row
 * is held two rows from the top: the page barely scrolls, content slides under a nearly
 * fixed viewport. That is also how every TV UI behaves.
 *
 * ## Focus across a mode switch
 *
 * Nothing to do. When the focused card unmounts, `FocusContext` falls back to the nearest
 * surviving entry, and when the whole screen is replaced the first element to register
 * claims focus. Both paths already existed for windowed rails.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CategoryDto, ContinueWatchingDto, DiscoveryDto } from '@filmodasi/shared';
import { useApi, usePagedCatalog } from '../../../api/hooks';
import { ErrorBox, Empty, Spinner } from '../../../components/ui';
import { detailPath } from '../../../catalog/detailPath';
import { TvCard, TvRail } from '../TvShell';
import { useFocus } from '../focus/FocusContext';
import { useFocusable } from '../focus/useFocusable';

/** Must match `.tvgrid` in styles.css. */
const COLUMNS = 6;
/** 5 × 6 = 30 cards, the ceiling the Phase 3 plan sets. */
const WINDOW_ROWS = 5;
/** Rows of focused-row headroom kept below the window before the next page is asked for. */
const PREFETCH_ROWS = 2;

const TITLES: Record<string, string> = {
  live: 'Canlı TV',
  movies: 'Filmler',
  series: 'Diziler',
};

export type LibrarySegment = 'live' | 'movies' | 'series';

/** What the grid is showing. All fields empty means the whole library. */
interface GridFilter {
  categoryId?: string;
  genre?: string;
  language?: string;
  label: string;
}

function Chip({
  id,
  label,
  count,
  active,
  onSelect,
}: {
  id: string;
  label: string;
  count?: number;
  active: boolean;
  onSelect: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({ id, group: 'lib:cat', onSelect });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvchip${active ? ' is-active' : ''}${focused ? ' is-focused' : ''}`}
      onClick={onSelect}
    >
      {label}
      {count != null && <span className="tvchip__count">{count}</span>}
    </button>
  );
}

function focusedIndex(focusedId: string | null): number | null {
  if (!focusedId?.startsWith('lib:item:')) return null;
  const n = Number(focusedId.slice('lib:item:'.length));
  return Number.isFinite(n) ? n : null;
}

function minutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} dk`;
  return `${Math.floor(m / 60)} sa ${m % 60} dk`;
}

function continueSubtitle(entry: ContinueWatchingDto): string {
  const { item, progress, nextEpisode } = entry;
  const episode =
    item.seasonNumber != null && item.episodeNumber != null
      ? `S${String(item.seasonNumber).padStart(2, '0')}B${String(item.episodeNumber).padStart(2, '0')} · `
      : '';
  if (nextEpisode) return `${episode}başlamadı`;
  const remaining =
    progress.durationSec && progress.durationSec > progress.positionSec
      ? `${minutes(progress.durationSec - progress.positionSec)} kaldı`
      : 'devam et';
  return `${episode}${remaining}`;
}

/** Rows. The default view. */
function DiscoverView({
  library,
  poster,
  onBrowse,
  onSeeAll,
}: {
  library: LibrarySegment;
  poster: boolean;
  onBrowse: () => void;
  onSeeAll: (filter: GridFilter) => void;
}) {
  const navigate = useNavigate();
  const discovery = useApi<DiscoveryDto & { continueWatching: ContinueWatchingDto[] }>(
    `/api/discovery/${library}`,
    [library],
  );

  if (discovery.loading && !discovery.data) return <Spinner />;
  if (discovery.error) return <ErrorBox message={discovery.error} onRetry={discovery.reload} />;

  const rows = discovery.data?.rows ?? [];
  if (rows.length === 0) {
    return <Empty>Burası boş. Bilgisayardan bir kaynak ekleyip eşitleyin.</Empty>;
  }

  return (
    <>
      <div className="tvchips">
        <Chip id="lib:cat:all" label="Tüm Kategoriler" active={false} onSelect={onBrowse} />
      </div>

      {rows.map((row, rowIndex) => {
        const cw = discovery.data?.continueWatching ?? [];

        if (row.kind === 'continue' && cw.length > 0) {
          return (
            <TvRail title={row.title} key={row.key}>
              {cw.map((entry, col) => (
                <TvCard
                  key={entry.item.id}
                  id={`lib:row:${rowIndex}:${col}`}
                  group={`lib:row:${row.key}`}
                  title={entry.series?.title ?? entry.item.title}
                  subtitle={continueSubtitle(entry)}
                  art={
                    entry.item.poster ??
                    entry.item.logo ??
                    entry.series?.poster ??
                    entry.series?.logo
                  }
                  poster={poster}
                  progressPercent={entry.nextEpisode ? 0 : entry.progress.percent}
                  onSelect={() => navigate(detailPath(entry.item))}
                />
              ))}
            </TvRail>
          );
        }

        return (
          <TvRail title={row.title} key={row.key}>
            {row.items.map((item, col) => (
              <TvCard
                key={item.id}
                // Position, not item id: the same film legitimately appears in two rows,
                // and a duplicate registry id makes one of the two cards unreachable.
                id={`lib:row:${rowIndex}:${col}`}
                group={`lib:row:${row.key}`}
                title={item.title}
                subtitle={item.year ? String(item.year) : null}
                art={item.poster ?? item.logo}
                poster={poster}
                onSelect={() => navigate(detailPath(item))}
              />
            ))}
            {row.seeAll && (
              <TvCard
                id={`lib:row:${rowIndex}:more`}
                group={`lib:row:${row.key}`}
                title="Tümünü Gör"
                subtitle={null}
                art={null}
                poster={poster}
                onSelect={() =>
                  onSeeAll({
                    genre: row.seeAll?.genre,
                    language: row.seeAll?.language,
                    label: row.title,
                  })
                }
              />
            )}
          </TvRail>
        );
      })}
    </>
  );
}

/** Provider categories and the windowed grid. The old screen, kept whole. */
function BrowseView({
  library,
  poster,
  filter,
  onFilter,
  onBack,
}: {
  library: LibrarySegment;
  poster: boolean;
  filter: GridFilter | null;
  onFilter: (f: GridFilter | null) => void;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const [windowStart, setWindowStart] = useState(0);
  const { focusedId } = useFocus();

  // Only fetched when the category chips are actually on screen.
  const categories = useApi<CategoryDto[]>(
    filter ? null : `/api/catalog/${library}/categories`,
    [library, filter === null],
  );

  const params: Record<string, string> = { sort: library === 'live' ? 'title' : 'newest' };
  if (filter?.categoryId) params.categoryId = filter.categoryId;
  if (filter?.genre) params.genre = filter.genre;
  if (filter?.language) params.language = filter.language;

  const grid = usePagedCatalog(`/api/catalog/${library}`, params);

  const loadedRows = Math.ceil(grid.items.length / COLUMNS);
  const index = focusedIndex(focusedId);
  const focusRow = index == null ? null : Math.floor(index / COLUMNS);

  // The window follows focus, clamped so it never scrolls past the loaded end.
  useEffect(() => {
    if (focusRow == null) return;
    const maxStart = Math.max(0, loadedRows - WINDOW_ROWS);
    setWindowStart(Math.min(Math.max(0, focusRow - 2), maxStart));
  }, [focusRow, loadedRows]);

  // A different filter is a different list; the window has to go back to the top with it.
  const filterKey = JSON.stringify(params);
  useEffect(() => {
    setWindowStart(0);
  }, [library, filterKey]);

  // Ask for the next page while there are still rows below to move onto. Waiting until
  // the last row would show the user a dead end for the length of a round trip.
  const { loading: gridLoading, hasMore, loadMore } = grid;
  useEffect(() => {
    if (focusRow == null || gridLoading || !hasMore) return;
    if (focusRow + WINDOW_ROWS + PREFETCH_ROWS >= loadedRows) loadMore();
  }, [focusRow, loadedRows, gridLoading, hasMore, loadMore]);

  const from = windowStart * COLUMNS;
  const visible = grid.items.slice(from, from + WINDOW_ROWS * COLUMNS);

  return (
    <>
      <div className="tvchips">
        {/* Names its destination, not the place you are standing. A chip reading
            "← Haber Kanallar" while showing Haber Kanallar parses as "go there" and does
            the opposite; where back belongs depends on whether this filter came from the
            category list or from a discovery row. */}
        <Chip
          id="lib:cat:back"
          label={filter?.categoryId ? '← Tüm Kategoriler' : '← Keşfet'}
          active={false}
          onSelect={() => (filter?.categoryId ? onFilter(null) : onBack())}
        />
        {filter && <span className="tvchips__now">{filter.label}</span>}
        {!filter &&
          categories.data?.map((c, i) => (
            <Chip
              key={c.id}
              id={`lib:cat:${i}`}
              label={c.name}
              count={c.itemCount}
              active={false}
              onSelect={() => onFilter({ categoryId: c.id, label: c.name })}
            />
          ))}
      </div>

      {categories.error && <ErrorBox message={categories.error} onRetry={categories.reload} />}
      {grid.error && <ErrorBox message={grid.error} />}

      {grid.items.length > 0 && (
        <p className="tv__section">
          {grid.total} sonuç
          {windowStart > 0 && ` · ${from + 1}. sıradan itibaren`}
        </p>
      )}

      <div className="tvgrid">
        {visible.map((item, i) => (
          <TvCard
            key={item.id}
            id={`lib:item:${from + i}`}
            group="lib:grid"
            title={item.title}
            subtitle={item.year ? String(item.year) : null}
            art={item.poster ?? item.logo}
            poster={poster}
            onSelect={() => navigate(detailPath(item))}
          />
        ))}
      </div>

      {grid.loading && grid.items.length === 0 && <Spinner />}
      {!grid.loading && grid.items.length === 0 && (
        <Empty>{filter ? 'Bu filtrede içerik yok.' : 'Burası boş.'}</Empty>
      )}
    </>
  );
}

export function TvLibraryPage({ library }: { library: LibrarySegment }) {
  const [browsing, setBrowsing] = useState(false);
  const [filter, setFilter] = useState<GridFilter | null>(null);
  const poster = library !== 'live';

  // Changing tab is a different library, so neither the mode nor the filter carries over.
  useEffect(() => {
    setBrowsing(false);
    setFilter(null);
  }, [library]);

  return (
    <>
      <h1 className="tv__heading">{TITLES[library] ?? library}</h1>

      {browsing ? (
        <BrowseView
          library={library}
          poster={poster}
          filter={filter}
          onFilter={setFilter}
          // Clears the filter as well: returning to the rows and keeping a filter around
          // means the next trip into Browse silently starts somewhere the user did not ask
          // for, which is the kind of state that makes Back feel unpredictable.
          onBack={() => {
            setFilter(null);
            setBrowsing(false);
          }}
        />
      ) : (
        <DiscoverView
          library={library}
          poster={poster}
          onBrowse={() => {
            setFilter(null);
            setBrowsing(true);
          }}
          onSeeAll={(f) => {
            setFilter(f);
            setBrowsing(true);
          }}
        />
      )}
    </>
  );
}
